import { KeenSliderPlugin, TrackDetails, TrackSlidesConfigOption, useKeenSlider } from 'keen-slider/react';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import useResizeObserver from 'use-resize-observer';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { Colors } from '../../../ui';
import { Flexer } from '../elements/LayoutElements';

export type KeenSlidesConfigureCallback = (size: number, slides: HTMLElement[]) => TrackSlidesConfigOption;

export type KeenSliderSlidesConfig = any;

export type KeenSliderProps = {
    children: ReactNode,
    auto?: boolean,
    loop?: boolean,
    slides?: KeenSliderSlidesConfig,
    onSlide?: (details: TrackDetails) => void,
};

const autoSlidePlugin: KeenSliderPlugin = (slider) => {
    const state = { timeout: null as any, over: false };
    const clearNextTimeout = () => clearTimeout(state.timeout);
    const nextTimeout = () => {
        clearTimeout(state.timeout);
        if (state.over) return;
        state.timeout = setTimeout(() => {
            slider.next();
        }, 10000);
    };
    slider.on('created', () => {
        slider.container.addEventListener('mouseover', () => {
            state.over = true;
            clearNextTimeout();
        });
        slider.container.addEventListener('mouseout', () => {
            state.over = false;
            nextTimeout();
        });
        nextTimeout();
    });
    slider.on('dragStarted', clearNextTimeout);
    slider.on('animationEnded', nextTimeout);
    slider.on('updated', nextTimeout);
};

const ButtonFrame = styled.div<{ disabled?: boolean, side: 'left' | 'right' }>`
    width: 2.5rem;
    height: 2.5rem;
    padding: .675rem;
    position: absolute;
    top: 50%;
    left: ${p => p.side === 'left' ? '.25rem' : '100%'};
    transform: translate(-50%, -50%);
    cursor: pointer;
    background: white;
    border: solid ${1 / 16}px ${Colors.border};
    border-radius: 50%;

    &:hover { background: ${Colors.hover}; }
    &:active { background: ${Colors.active}; }

    svg {
        fill: ${Colors.textLighter};
    }

    transition: opacity 0.2s;

    ${p => p.disabled && css`
        opacity: 0;
        pointer-events: none;
    `};
`;

const Button = (props: { disabled: boolean, side: 'left' | 'right', onClick(e: any): void }) => {
    // const disabled = disabled ? ' arrow--disabled' : '';
    return <ButtonFrame {...props}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            {props.side === 'left' && (
                <path d="M16.67 0l2.83 2.829-9.339 9.175 9.339 9.167-2.83 2.829-12.17-11.996z" />
            )}
            {props.side === 'right' && (
                <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
            )}
        </svg>
    </ButtonFrame>;
};

const useKeenSliderContainerWidth = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    useResizeObserver({
        ref,
        onResize: (entry) => {
            setWidth(entry.width);
        }
    });
    return [ref, width] as const;
};

const Item = ({ children }: { children: ReactNode }) =>
    <div className="keen-slider__slide">
        {children}
    </div>;

const Base = ({ children, auto, onSlide, ...rest }: KeenSliderProps) => {

    const [loaded, setLoaded] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [containerRef, containerWidth] = useKeenSliderContainerWidth();
    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
        {
            mode: 'snap',
            rtl: false,
            initial: 0,
            ...rest,
            created() {
                setLoaded(true);
            },
            slideChanged(slider) {
                setCurrentSlide(slider.track.details.rel);
            },
        },
        auto ? [autoSlidePlugin] : []
    );

    const [sliderKey, setSliderKey] = useState(uuid());
    useEffect(() => setSliderKey(uuid()), [containerWidth, children]);

    return <Flexer relative>
        <Flexer relative ref={containerRef}>
            {!!containerWidth &&
                <div key={sliderKey} ref={sliderRef} className="keen-slider" style={{ maxWidth: containerWidth }}>
                    {children}
                </div>
            }
        </Flexer>
        {loaded && instanceRef.current && <>
            <Button side="left"
                onClick={(e: any) =>
                    e.stopPropagation() || instanceRef.current?.prev()
                }
                disabled={currentSlide === 0}
            />
            <Button side="right"
                onClick={(e: any) =>
                    e.stopPropagation() || instanceRef.current?.next()
                }
                disabled={
                    currentSlide ===
                    instanceRef.current?.track?.details?.slides?.length - 1
                }
            />
        </>}
    </Flexer>;
};

export const KeenSlider = Object.assign(Base, {
    Item
});
